import { useAtom } from "jotai";
import { TeamMember } from "@quantizr-engineering/common/types/user";
import apiClient from "./apiClient";
import {
  artistsAtom,
  teamMembersAtom,
  teamsForMemberAtom,
} from "../atoms/teamAtoms";
import { FIVE_MINUTES_MS } from "../utils/consts";
import { useEffect } from "react";
import { toast } from "sonner";

export const useTeamService = () => {
  const [teamMembers, setTeamMembers] = useAtom(teamMembersAtom);
  const [teamsForMember, setTeamsForMember] = useAtom(teamsForMemberAtom);
  const [artists, setArtists] = useAtom(artistsAtom);

  useEffect(() => {
    cacheGetTeam();
    cacheGetTeamsForMember();
  });
  const getTeam = async () => {
    setTeamMembers((prev) => ({ ...prev, loading: true }));
    setArtists((prev) => ({ ...prev, loading: true }));
    try {
      const response = await apiClient.get("/team/members", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setTeamMembers((prev) => ({
        loading: false,
        data: response.data.teamMembers,
        lastFetched: Date.now(),
      }));
      setArtists((prev) => ({
        loading: false,
        data: response.data.artists,
        lastFetched: Date.now(),
      }));
      return response;
    } catch (error) {
      setTeamMembers((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      setArtists((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      console.error("Failed to load team members");
      throw error;
    }
  };

  const cacheGetTeam = async () => {
    if (!!teamMembers.loading) {
      return;
    }
    if (
      !teamMembers.lastFetched ||
      Date.now() - teamMembers.lastFetched > FIVE_MINUTES_MS
    ) {
      await getTeam();
    }
  };

  const getTeamsForMember = async () => {
    setTeamsForMember((prev) => ({ ...prev, loading: true }));
    try {
      const response = await apiClient.get("/team/member/teams", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setTeamsForMember((prev) => ({
        loading: false,
        data: response.data,
        lastFetched: Date.now(),
      }));
      return response;
    } catch (error) {
      setTeamsForMember((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      console.error("Failed to load teams for member");
      throw error;
    }
  };

  const cacheGetTeamsForMember = async () => {
    if (!!teamsForMember.loading) {
      return;
    }
    if (
      !teamsForMember.lastFetched ||
      Date.now() - teamsForMember.lastFetched > FIVE_MINUTES_MS
    ) {
      await getTeamsForMember();
    }
  };

  const createNewTeam = async (teamName: string) => {
    try {
      const response = await apiClient.post(
        `/team`,
        {
          name: teamName,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      toast.success(response.headers["qz-message"]);
      setTeamsForMember((prev) => ({
        ...prev,
        data: [...(prev.data ?? []), response.data],
      }));
      return response;
    } catch (error) {
      throw new Error("Failed to create new team");
    }
  };

  const patchTeamMembers = async (
    updatedTeamMembers: Partial<TeamMember>[]
  ) => {
    try {
      const response = await apiClient.patch(
        `/team/members`,
        {
          teamMembers: updatedTeamMembers.map((tm) => {
            return {
              ...tm,
              roles: typeof tm.roles === "string" ? [tm.roles] : tm.roles,
            };
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      toast.success(response.headers["qz-message"]);
      await getTeam();
      return response;
    } catch (error) {
      throw new Error("Failed to update team member");
    }
  };

  return {
    teamMembers: teamMembers.data,
    teamMembersLoading: teamMembers.loading,
    artists: artists.data,
    artistsLoading: artists.loading,
    getTeam,
    createNewTeam,
    patchTeamMembers,
    cacheGetTeam,
    teamsForMember: teamsForMember.data,
  };
};
